import type { FacebookDestinationPlatformData } from './destination-details.interfaces';

export const FACEBOOK_PAGE_VIDEO = 'PageVideo';
export const FACEBOOK_PAGE_REEL = 'PageReel';
export const FACEBOOK_CONTENT_CATEGORY_NONE = '';

/** Per-publication Facebook override stored in publish_metadata_overrides_json.destinationSettings. */
export interface FacebookPublicationSettings {
  publish_target: string | null;
  embeddable?: boolean | null;
  no_story?: boolean | null;
  content_category?: string | null;
}

export function mapFacebookDefaultsToPublicationSettings(
  defaults: FacebookDestinationPlatformData | null | undefined,
): FacebookPublicationSettings {
  const publishTarget =
    defaults?.default_publish_target?.trim() || FACEBOOK_PAGE_VIDEO;
  return {
    publish_target: publishTarget,
    embeddable: defaults?.embeddable ?? null,
    no_story: defaults?.no_story ?? null,
    content_category: defaults?.content_category ?? null,
  };
}

export function normalizeFacebookPublicationSettings(
  settings: FacebookPublicationSettings,
): FacebookPublicationSettings {
  const publishTarget = settings.publish_target ?? FACEBOOK_PAGE_VIDEO;
  if (publishTarget === FACEBOOK_PAGE_REEL) {
    return { publish_target: FACEBOOK_PAGE_REEL };
  }
  return {
    publish_target: FACEBOOK_PAGE_VIDEO,
    embeddable: settings.embeddable ?? null,
    no_story: settings.no_story ?? null,
    content_category: settings.content_category ?? null,
  };
}

/** Merges saved override (publication or legacy tenant shape) with destination defaults. */
export function resolveFacebookPublicationSettings(
  saved: unknown,
  defaults: FacebookDestinationPlatformData | null | undefined,
): FacebookPublicationSettings {
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    const obj = saved as Record<string, unknown>;
    if (typeof obj.publish_target === 'string' || obj.publish_target === null) {
      return normalizeFacebookPublicationSettings({
        publish_target: (obj.publish_target as string | null) ?? FACEBOOK_PAGE_VIDEO,
        embeddable: obj.embeddable as boolean | null | undefined,
        no_story: obj.no_story as boolean | null | undefined,
        content_category: obj.content_category as string | null | undefined,
      });
    }
    if ('default_publish_target' in obj) {
      return mapFacebookDefaultsToPublicationSettings(
        obj as unknown as FacebookDestinationPlatformData,
      );
    }
  }
  return mapFacebookDefaultsToPublicationSettings(defaults);
}

export function isFacebookReelPublishTarget(publishTarget: string | null | undefined): boolean {
  return publishTarget === FACEBOOK_PAGE_REEL;
}
